import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { QUERY_REGISTRY } from '@/core/graphQL/registry'
import { useFormatters } from '@hooks/useFormatters'
import { INTERNAL_CONSTANTS } from '@constants/internalConstants'

// Builds a component-facing read hook from a registry entry. All the TanStack +
// session-gating + formatting wiring lives here once; feature modules are just
// one-line `createQueryHook('feature.op')` exports.
export function createQueryHook(registryKey) {
  const entry = QUERY_REGISTRY[registryKey]
  if (!entry) {
    throw new Error(`[createQueryHook] Unknown query key: "${registryKey}"`)
  }

  const { service, mapper, key, enabled } = entry

  return function useGeneratedQuery(params = {}, options = {}) {
    // Never fire before the session is authenticated — globals would be null.
    const ready = useSelector(
      (state) =>
        state.session.authenticationStatus ===
        INTERNAL_CONSTANTS.AUTHENTICATION_STATUS.SUCCEEDED
    )
    const formatters = useFormatters()

    const select = useCallback(
      (raw) => (mapper ? mapper(raw, formatters) : raw),
      [formatters]
    )

    const { enabled: enabledOption, ...restOptions } = options

    return useQuery({
      queryKey: key(params),
      queryFn: () => service(params),
      select,
      placeholderData: keepPreviousData,
      enabled:
        ready && (enabled ? enabled(params) : true) && (enabledOption ?? true),
      ...restOptions,
    })
  }
}
